/**
 * ProductBarcodePage - Barcode management page
 * Refactored to use useProductBarcode hook and extracted components
 * Includes barcode simulator for generating test barcodes (4 models)
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductBarcode } from '../hooks/useProductBarcode';
import { useBarcodeValidation } from '../hooks/useBarcodeValidation';
import {
  ValidationPhotoManager,
  BarcodeInputSection,
  ParsedDataDisplay,
  ExpectedDataForm,
  ValidationResultsPanel,
  SavedCasesManager,
  CaseNavigator,
} from '../components/barcode-validation';
import {
  BarcodeHeader,
  BarcodeTabs,
  BarcodeFilters,
  ProductsView,
  ContagensListView,
  ClienteAccordion,
  BarcodeModelSelector,
  BarcodeExporter,
  DataMatrixGenerator,
} from '../components/product-barcode';
import type { ExportableBarcode } from '../components/product-barcode';

type ViewMode = 'accordion' | 'cards' | 'list';
type ActiveTab = 'produtos' | 'estoque' | 'simulador' | 'validacao';

export const ProductBarcodePage = () => {
  const navigate = useNavigate();

  // Tab and view state
  const [activeTab, setActiveTab] = useState<ActiveTab>('produtos');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('products_barcode_view_mode');
    return (saved === 'accordion' || saved === 'cards' || saved === 'list') ? saved : 'accordion';
  });

  // Barcode simulator state
  const [exportBarcodes, setExportBarcodes] = useState<ExportableBarcode[]>([]);
  const [lastGeneratedDataMatrix, setLastGeneratedDataMatrix] = useState<string | null>(null);

  // Hook for all data and filter management
  const {
    products,
    stockItems,
    loading,
    isAdmin,
    filteredProducts,
    filteredStockItems,
    clienteOptions,
    fabricanteOptions,
    categoriaOptions,
    selectedClientes,
    setSelectedClientes,
    selectedFabricantes,
    setSelectedFabricantes,
    selectedCategorias,
    setSelectedCategorias,
    searchNome,
    setSearchNome,
    searchCodigo,
    setSearchCodigo,
    formatPrice,
    formatDate,
  } = useProductBarcode();

  // Barcode validation hook
  const validation = useBarcodeValidation();

  // Count active filters
  const activeFiltersCount =
    selectedClientes.length +
    selectedFabricantes.length +
    selectedCategorias.length +
    (searchNome ? 1 : 0) +
    (searchCodigo ? 1 : 0);

  // Barcode simulator handlers
  const handleBarcodeGenerated = useCallback((barcode: ExportableBarcode) => {
    setExportBarcodes((prev) => [...prev, barcode]);
    // If it's a DataMatrix (model C), store the data for rendering
    if (barcode.model === 'C') {
      // GS1 Specification: Parentheses are human-readable notation ONLY
      // Real barcode data must NOT contain parentheses
      // bwip-js 'gs1datamatrix' symbology automatically handles:
      // - FNC1 at the start (mandatory for GS1)
      // - FNC1 separators between variable-length fields
      // We just need to provide clean data without parentheses
      const gs1Data = barcode.codigoCompleto.replace(/[()]/g, '');
      setLastGeneratedDataMatrix(gs1Data);
    }
  }, []);

  const handleRemoveBarcode = useCallback((index: number) => {
    setExportBarcodes((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleClearBarcodes = useCallback(() => {
    setExportBarcodes([]);
    setLastGeneratedDataMatrix(null);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <BarcodeHeader
        isAdmin={isAdmin}
        onBack={() => navigate('/')}
      />

      {/* Tabs and Controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <BarcodeTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          viewMode={viewMode}
          setViewMode={setViewMode}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          activeFiltersCount={activeFiltersCount}
        />

        {/* Filter Panel */}
        {showFilters && (
          <BarcodeFilters
            clienteOptions={clienteOptions}
            fabricanteOptions={fabricanteOptions}
            categoriaOptions={categoriaOptions}
            selectedClientes={selectedClientes}
            setSelectedClientes={setSelectedClientes}
            selectedFabricantes={selectedFabricantes}
            setSelectedFabricantes={setSelectedFabricantes}
            selectedCategorias={selectedCategorias}
            setSelectedCategorias={setSelectedCategorias}
            searchNome={searchNome}
            setSearchNome={setSearchNome}
            searchCodigo={searchCodigo}
            setSearchCodigo={setSearchCodigo}
            resultsCount={activeTab === 'produtos' ? filteredProducts.length : filteredStockItems.length}
            totalCount={activeTab === 'produtos' ? products.length : stockItems.length}
            resultLabel={activeTab === 'produtos' ? 'produtos' : 'itens em estoque'}
          />
        )}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {activeTab === 'validacao' ? (
          <div className="space-y-5">
            <CaseNavigator
              cases={validation.savedCases}
              currentIndex={validation.currentCaseIndex}
              onNavigate={validation.navigateCase}
              onLoadSamples={validation.loadSamples}
            />
            <ValidationPhotoManager
              photos={validation.photos}
              onAddPhoto={validation.addPhoto}
              onRemovePhoto={validation.removePhoto}
            />
            <BarcodeInputSection
              rawBarcode={validation.rawBarcode}
              scanType={validation.parsedData?.scanType ?? null}
              onRawChange={validation.setRawBarcode}
              onParse={() => validation.parseBarcode(validation.rawBarcode)}
            />
            <ParsedDataDisplay data={validation.parsedData} />
            <ExpectedDataForm
              data={validation.expectedData}
              onFieldChange={validation.updateExpectedField}
              onClear={validation.clearExpected}
            />
            <ValidationResultsPanel
              results={validation.validationResults}
              onValidate={validation.runValidation}
              canValidate={!!validation.parsedData}
            />
            <SavedCasesManager
              cases={validation.savedCases}
              onSave={validation.saveCase}
              onLoad={validation.loadCase}
              onDelete={validation.deleteCase}
            />
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'produtos' && viewMode === 'accordion' && (
              <ClienteAccordion
                products={filteredProducts}
              />
            )}

            {activeTab === 'produtos' && (viewMode === 'cards' || viewMode === 'list') && (
              <ProductsView
                products={filteredProducts}
                isAdmin={isAdmin}
                totalProducts={products.length}
                viewMode={viewMode}
                formatPrice={formatPrice}
              />
            )}

            {activeTab === 'estoque' && (
              <ContagensListView
                viewMode={viewMode}
                formatDate={formatDate}
              />
            )}

            {activeTab === 'simulador' && (
              <div className="space-y-6">
                {/* Barcode Model Selector */}
                <BarcodeModelSelector
                  products={filteredProducts}
                  onBarcodeGenerated={handleBarcodeGenerated}
                />

                {/* DataMatrix Preview (when model C is generated) */}
                {lastGeneratedDataMatrix && (
                  <DataMatrixGenerator
                    data={lastGeneratedDataMatrix}
                    size={180}
                    showData={true}
                  />
                )}

                {/* Export List */}
                <BarcodeExporter
                  barcodes={exportBarcodes}
                  onRemove={handleRemoveBarcode}
                  onClear={handleClearBarcodes}
                />
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
};
